import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BACKEND = BACKEND_URL;

const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = localStorage.getItem("dynaqr_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const setToken = (t) => localStorage.setItem("dynaqr_token", t);
export const clearToken = () => localStorage.removeItem("dynaqr_token");
export const goTarget = (slug) => `${API}/go/${slug}`;

export const toYMD = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const api = {
  // auth
  register: (data) => http.post("/auth/register", data).then((r) => r.data),
  login: (data) => http.post("/auth/login", data).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  forgotPassword: (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, password) => http.post("/auth/reset-password", { token, password }).then((r) => r.data),
  changePassword: (data) => http.post("/auth/change-password", data).then((r) => r.data),
  // profile
  getProfile: () => http.get("/profile").then((r) => r.data),
  updateProfile: (data) => http.put("/profile", data).then((r) => r.data),
  // subscription / payments
  getPlans: () => http.get("/plans").then((r) => r.data),
  paymentInfo: () => http.get("/payment-info").then((r) => r.data),
  subscription: () => http.get("/subscription").then((r) => r.data),
  submitPayment: (data) => http.post("/payments", data).then((r) => r.data),
  myPayments: () => http.get("/payments").then((r) => r.data),
  uploadScreenshot: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/payments/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  fileBlobUrl: async (path) => {
    const res = await http.get(`/files/${path}`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  },
  // notifications
  notifications: () => http.get("/notifications").then((r) => r.data),
  markRead: () => http.post("/notifications/read").then((r) => r.data),
  // qr + overview
  overview: (qrId) => http.get("/overview", { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  getQr: (qrId) => http.get("/qr", { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  updateQr: (data, qrId) => http.put("/qr", data, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  // multi-qr
  listQrs: () => http.get("/qrs").then((r) => r.data),
  createQr: (data) => http.post("/qrs", data).then((r) => r.data),
  deleteQr: (id) => http.delete(`/qrs/${id}`).then((r) => r.data),
  // events
  listEvents: (qrId) => http.get("/events", { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  createEvent: (data, qrId) => http.post("/events", data, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  updateEvent: (id, data, qrId) => http.put(`/events/${id}`, data, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  deleteEvent: (id, qrId) => http.delete(`/events/${id}`, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  activateEvent: (id, qrId) => http.post(`/events/${id}/activate`, {}, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  // schedules
  listSchedules: (qrId) => http.get("/schedules", { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  createSchedule: (data, qrId) => http.post("/schedules", data, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  deleteSchedule: (id, qrId) => http.delete(`/schedules/${id}`, { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  clearCompletedSchedules: (qrId) => http.delete("/schedules/completed", { params: qrId ? { qr_id: qrId } : {} }).then((r) => r.data),
  // analytics
  analytics: (range, qrId) => {
    const params = {};
    if (range?.start) params.start = range.start;
    if (range?.end) params.end = range.end;
    if (qrId) params.qr_id = qrId;
    return http.get("/analytics", { params }).then((r) => r.data);
  },
  clearScans: (days, qrId) => http.delete("/scans", { params: { ...(days ? { older_than_days: days } : {}), ...(qrId ? { qr_id: qrId } : {}) } }).then((r) => r.data),
  analyticsAggregate: (range) => {
    const params = {};
    if (range?.start) params.start = range.start;
    if (range?.end) params.end = range.end;
    return http.get("/analytics/aggregate", { params }).then((r) => r.data);
  },
  downloadCsv: async (eventId, range, qrId) => {
    const params = {};
    if (eventId) params.event_id = eventId;
    if (range?.start) params.start = range.start;
    if (range?.end) params.end = range.end;
    if (qrId) params.qr_id = qrId;
    const res = await http.get("/export/scans.csv", { params, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dynaqr_scans.csv";
    a.click();
    URL.revokeObjectURL(url);
  },
  // public scan
  getPreview: (slug) => http.get(`/preview/${slug}`).then((r) => r.data),
  recordScan: (slug, data) => http.post(`/scan/${slug}`, data).then((r) => r.data),
  // admin
  adminOverview: () => http.get("/admin/overview").then((r) => r.data),
  adminUsers: (params) => http.get("/admin/users", { params }).then((r) => r.data),
  adminUser: (id) => http.get(`/admin/users/${id}`).then((r) => r.data),
  adminDisable: (id) => http.post(`/admin/users/${id}/disable`).then((r) => r.data),
  adminActivate: (id) => http.post(`/admin/users/${id}/activate`).then((r) => r.data),
  adminExtend: (id, data) => http.post(`/admin/users/${id}/extend`, data).then((r) => r.data),
  adminPayments: (status) => http.get("/admin/payments", { params: status ? { status } : {} }).then((r) => r.data),
  adminApprove: (id) => http.post(`/admin/payments/${id}/approve`).then((r) => r.data),
  adminReject: (id, reason) => http.post(`/admin/payments/${id}/reject`, { reason }).then((r) => r.data),
  adminSubscriptions: () => http.get("/admin/subscriptions").then((r) => r.data),
  adminPlans: () => http.get("/admin/plans").then((r) => r.data),
  adminQrcodes: () => http.get("/admin/qrcodes").then((r) => r.data),
  adminSettings: () => http.get("/admin/settings").then((r) => r.data),
  updateAdminSettings: (data) => http.put("/admin/settings", data).then((r) => r.data),
  // policies
  listPolicies: () => http.get("/policies").then((r) => r.data),
  getPolicy: (slug) => http.get(`/policies/${slug}`).then((r) => r.data),
  adminPolicies: () => http.get("/admin/policies").then((r) => r.data),
  updatePolicy: (slug, data) => http.put(`/admin/policies/${slug}`, data).then((r) => r.data),
  // use-case pages
  listPages: () => http.get("/pages").then((r) => r.data),
  getTestimonials: () => http.get("/testimonials").then((r) => r.data),
  publicStats: () => http.get("/public/stats").then((r) => r.data),
  adminGetTestimonials: () => http.get("/admin/testimonials").then((r) => r.data),
  updateTestimonials: (testimonials) => http.put("/admin/testimonials", { testimonials }).then((r) => r.data),
  getPage: (slug) => http.get(`/pages/${slug}`).then((r) => r.data),
  adminPages: () => http.get("/admin/pages").then((r) => r.data),
  createPage: (data) => http.post("/admin/pages", data).then((r) => r.data),
  updatePage: (slug, data) => http.put(`/admin/pages/${slug}`, data).then((r) => r.data),
  deletePage: (slug) => http.delete(`/admin/pages/${slug}`).then((r) => r.data),
};
